import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import type { Meta } from "./types";

interface MetaState {
  meta: Meta | null;
  /** Re-fetch after editing categories, pick-lists or preferences. */
  refresh: () => Promise<void>;
}

const MetaContext = createContext<MetaState>({ meta: null, refresh: async () => {} });

/**
 * Reference data the pickers are built from. Almost all of it is the user's own
 * editable rows, so this reloads when the signed-in user changes and exposes a
 * refresh for Settings to call after an edit.
 */
export function MetaProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [meta, setMeta] = useState<Meta | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setMeta(null);
      return;
    }
    try {
      setMeta(await api.get<Meta>("/meta"));
    } catch {
      setMeta(null);
    }
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo(() => ({ meta, refresh }), [meta, refresh]);
  return <MetaContext.Provider value={value}>{children}</MetaContext.Provider>;
}

export function useMeta(): Meta | null {
  return useContext(MetaContext).meta;
}

export function useMetaRefresh(): () => Promise<void> {
  return useContext(MetaContext).refresh;
}

/** Categories still selectable — archived ones stay valid on existing rows. */
export function useActiveCategories() {
  const meta = useMeta();
  return meta?.categories.filter((c) => !c.isArchived) ?? [];
}

export function useCategoryLabel(): (key: string) => string {
  const meta = useMeta();
  return (key: string) => meta?.categories.find((c) => c.key === key)?.label ?? key;
}
